// src/views/menage/ControleView.jsx — CONTRÔLE DES MÉNAGES, l'écran du
// CONTRÔLEUR. Recalage complet sur la maquette validée (GO Dimitri
// 2026-08-18) — il se tient d'une main, debout, dans le logement.
//
// LES ÉCRANS :
//   1. LISTE « À contrôler » — bandeau navy, ordre chronologique de FIN de
//      ménage, le compte de checklist s'annonce dès la liste (« Karim ·
//      17 sur 18 coché »), CE QUI PRESSE aussi (« Léa arrive à 16h — 2 h pour
//      reprendre » terracotta / « Personne avant demain » ciel). DEUX BOUTONS
//      par carte : « Rien à signaler » (LE cas fréquent, un geste) et
//      « Ouvrir ». Un groupe « Déjà contrôlés » estompé en bas.
//   2. CONTRÔLE (au clic « Ouvrir ») — ce qui presse en tête, le bouton
//      « Voir ce que Karim a coché · 17 sur 18 · une ligne non cochée »
//      (compte TERRACOTTA quand une ligne manque), les deux verdicts 46 px.
//   3. CHECKLIST — lecture seule, badge « Lecture » NAVY PLEIN, lignes non
//      cochées en ambre. AUCUN bouton de verdict ici.
//   4. SIGNALEMENT — un écran par question : les catégories (liste verticale,
//      l'avertissement « non cochée » par catégorie), puis UN POINT À LA FOIS
//      (bandeau terracotta, progression, un seul champ + photo), puis le
//      RÉCAPITULATIF (crayon par point, « Qui reprend ? » au dernier moment,
//      « Envoyer les N points »).
//
// ⚠️ ANNULATION DIFFÉRÉE (décision Dimitri) : « Rien à signaler » ne part
// qu'APRÈS le délai d'annulation — la carte vire au vert sur place avec
// « Annuler » ; application fermée pendant le délai = RIEN n'est envoyé.
//
// ⚠️ HEURE DU CONTRAT SEULEMENT : toute heure d'arrivée affichée ici est
// l'heure THÉORIQUE (cfg) servie par le serveur — jamais l'heure réelle
// voyageur (réservée orchestrateur/admin, arbitrage Dimitri 2026-08-18).
//
// ⚠️ JAMAIS un faux « non cochée » : l'avertissement par catégorie repose sur
// une correspondance de NOMS entre les pièces de la checklist (configurées
// par logement) et les 6 catégories fixes — correspondance introuvable ⇒ la
// catégorie ne porte RIEN (mapperNonCochees, exporté et testé).
//
// ⚠️ ANONYMAT : rien ici ne suggère que l'agent verra « qui » a contrôlé —
// l'agent reçoit les points, jamais l'identité du contrôleur (serveur).

import { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { DS } from "../../lib/ui"
import { heureFinFenetreFR } from "../../lib/controle-fenetre"
import CarteTitrePage from '../../components/CarteTitrePage'
import BasculeEspace from '../../components/BasculeEspace'
import ControleBilan from './ControleBilan'
import ControleListeLarge from './ControleListeLarge'
import { designerControleurRun, designerControleurLogement } from '../../lib/panneau-menage-api'
import { fetchPoolPrenoms } from '../../lib/pool-prenoms'

import { fetchLogInfo } from "../../lib/menage-log-info-cache"
import { nomProduit } from "../../lib/consommables-helpers"
import {
  fetchAControler, fetchRunChecklist, envoyerControle,
  fetchMesControles, uploaderPhotoControle, fichierEnBase64, MAX_PHOTOS_PAR_POINT,
} from "../../lib/controle-api"
import { compressImage } from "../../lib/image-compression"
import {
  ChevronRight, Eye, Check, Circle, CircleAlert, CircleCheck,
  RefreshCw, ClipboardCheck, X, Camera, Pencil,
  ShowerHead, Utensils, BedDouble, Shirt, Footprints, MoreHorizontal, ChartColumn,
} from "lucide-react"

// ≥ 900 px = l'écran large gagne sa forme ; dessous, le dessin mobile est roi.
function useLargeEcran() {
  const [large, setLarge] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(min-width: 900px)').matches : false)
  useEffect(() => {
    if (!window.matchMedia) return
    const mq = window.matchMedia('(min-width: 900px)')
    const suivre = (e) => setLarge(e.matches)
    mq.addEventListener('change', suivre)
    return () => mq.removeEventListener('change', suivre)
  }, [])
  return large
}

// ── LA GRILLE DE COULEURS (maquette validée — rendu à montrer avant d'acter) ──
// Vert #7fc4a3 = le validé ; terracotta = ce qui pose problème ; ciel #5c8ab8 =
// l'actionnable ; bandeaux navy en dégradé (les cartes de titre), qui virent
// TERRACOTTA à l'entrée du signalement.
const C = {
  navy: '#3d5166', fonce: '#2e3d4d', bord: '#d5e3ee', bordFort: '#c3d5e4',
  filet: '#eef1f4', texte: '#233240', muet: '#98a2ad',
}
const VERT = '#7fc4a3'
const VERT_SOMBRE = '#1a4f3a'
const VERT_FOND = '#f0f7f3'
const VERT_BORD = '#c9e0d4'
const TERRA = '#c1512d'
const TERRA_SOMBRE = '#8f2f1d'
const TERRA_FOND = '#fdf4f0'
const TERRA_BORD = '#b8412b'
const CIEL = '#5c8ab8'
const AMBRE = { fond: '#fdf7ee', bordure: '#d4a24c', texte: '#8a5f19' }
// Le dégradé des cartes de titre (CarteTitrePage) — même signature visuelle.
const TERRA_DEGRADE = 'linear-gradient(115deg, #6e2413 0%, #8f2f1d 40%, #c1512d 100%)'

const CIBLE = 44
const CIBLE_VERDICT = 46
// L'envoi de « Rien à signaler » part APRÈS ce délai — « Annuler » l'arrête,
// fermer l'application aussi (décision Dimitri : c'est le bon comportement).
export const DELAI_ANNULATION_MS = 5000

const PHOTO_CIBLE_OCTETS = 2.5 * 1024 * 1024

function libererApercu(apercu) {
  try {
    if (apercu && typeof URL !== 'undefined' && URL.revokeObjectURL) URL.revokeObjectURL(apercu)
  } catch (e) { console.error('[controle] revoke aperçu', e?.message || e) }
}

export function construirePayloadPoints(pts) {
  return (pts || []).map(({ categorie, texte, photos }) => {
    const paths = (photos || [])
      .filter((p) => p.statut === 'ok' && p.path)
      .map((p) => p.path)
    return paths.length ? { categorie, texte, photos: paths } : { categorie, texte }
  })
}

// Les 6 catégories VALIDES de controle_create — même liste que le serveur.
export const CATEGORIES_POINTS = [
  { id: 'salle_de_bain', label: 'Salle de bain', icone: ShowerHead },
  { id: 'cuisine', label: 'Cuisine', icone: Utensils },
  { id: 'chambres', label: 'Chambres', icone: BedDouble },
  { id: 'linge', label: 'Linge', icone: Shirt },
  { id: 'sols', label: 'Sols', icone: Footprints },
  { id: 'autre', label: 'Autre', icone: MoreHorizontal },
]

// « 21 août » — LE format de date (règle du dessin unique appliquée aux
// dates, tri Dimitri 2026-08-21) : ni jour de semaine, ni année.
export function dateCourteControle(iso) {
  const t = Date.parse(`${String(iso || '').slice(0, 10)}T12:00:00Z`)
  if (Number.isNaN(t)) return ''
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', timeZone: 'Europe/Paris' }).format(t)
}

export function humaniserId(id) {
  const s = String(id || '').replace(/[_-]+/g, ' ').trim()
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''
}

export function lignesChecklist(itemsChecked) {
  const items = itemsChecked && typeof itemsChecked === 'object' ? itemsChecked : {}
  const lignes = []
  for (const [cle, valeur] of Object.entries(items)) {
    if (Array.isArray(valeur)) {
      if (valeur.length === 0) {
        lignes.push({ id: cle, label: humaniserId(cle), coche: false })
      } else {
        for (const item of valeur) {
          lignes.push({
            id: `${cle}:${item}`,
            label: `${humaniserId(cle)} — ${humaniserId(item)}`,
            coche: true,
          })
        }
      }
    } else if (typeof valeur === 'boolean') {
      lignes.push({ id: cle, label: humaniserId(cle), coche: valeur })
    }
  }
  return lignes
}

export function heureFR(iso) {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(d).replace(':', 'h')
}

/** « 16:00 » → « 16h », « 16:30 » → « 16h30 ». */
export function heureCourte(hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ''))
  if (!m) return null
  return Number(m[2]) === 0 ? `${Number(m[1])}h` : `${Number(m[1])}h${m[2]}`
}

// ── CE QUI PRESSE — calculé sur l'heure THÉORIQUE servie par le serveur. ─────
// `prochaine` : undefined = le serveur ne sert pas encore l'info → RIEN (une
// absence n'est pas une cause) ; null = pas d'arrivée à l'horizon → calme.
// SEUIL « ça presse » SANS réservation : l'échéance du contrat approche à
// 2 h (correction Dimitri 2026-08-21 — un logement sans voyageur peut en
// recevoir un ce soir ; avec une réservation aujourd'hui, presse comme avant).
export const SEUIL_PRESSE_CONTRAT_MIN = 120

export function urgenceDe(prochaine, nowMin, todayIso, heureContrat, scheduledDate = null) {
  // PAS de réservation aujourd'hui (absente, demain, ou info non servie).
  // ⚠️ UNE SEULE BORNE (correction Dimitri 2026-08-22) : l'échéance écrite est
  // TOUJOURS la fin de fenêtre (contrat + 12 h, celle du serveur). L'heure du
  // contrat, elle, se dit pour ce qu'elle est : une ARRIVÉE possible. Et le
  // sujet est nommé — « aucune arrivée prévue aujourd'hui », jamais un
  // « personne » ambigu à côté de « Désigner ».
  if (prochaine === undefined || !prochaine || !prochaine.date || prochaine.date > todayIso) {
    const m = /^(\d{1,2}):(\d{2})/.exec(String(heureContrat || ''))
    if (m) {
      const h = heureCourte(heureContrat)
      const hFin = heureFinFenetreFR(scheduledDate || todayIso, heureContrat)
      const borne = hFin ? `à faire avant ${hFin}` : 'fenêtre de contrôle inconnue'
      const borneMaj = `${borne.charAt(0).toUpperCase()}${borne.slice(1)}`
      // Info non servie (undefined) → l'échéance seule, sans affirmer l'absence.
      const reserve = prochaine === undefined ? '' : " · aucune arrivée prévue aujourd'hui"
      const reste = Number(m[1]) * 60 + Number(m[2]) - nowMin
      if (reste <= 0) {
        return { presse: true, liste: `arrivée possible depuis ${h} — ${borne}${reserve}`,
          ecran: `Le contrat permet une arrivée depuis ${h} — une dernière minute reste possible. ${borneMaj}.` }
      }
      if (reste <= SEUIL_PRESSE_CONTRAT_MIN) {
        const resteTexte = reste < 60 ? "moins d'une heure" : `${Math.floor(reste / 60)} h`
        return { presse: true, dansHeures: Math.max(1, Math.floor(reste / 60)),
          liste: `arrivée possible dès ${h} — ${resteTexte}${reserve}`,
          ecran: `Une arrivée reste possible dès ${h} (heure du contrat) — dans ${resteTexte}. ${borneMaj}.` }
      }
      return { presse: false, liste: `${borne}${reserve}`, ecran: null }
    }
    // Heure du contrat NON CONFIGURÉE (null servi) : l'écran le DIT — il
    // n'invente jamais une heure (correction Dimitri 2026-08-21). `manque`
    // teinte la ligne en ambre (un trou de configuration, pas une urgence).
    if (heureContrat === null) {
      return { presse: false, manque: true,
        liste: "heure d'arrivée du contrat non configurée", ecran: null }
    }
    // Champ non servi du tout (vieux payload) : comportement historique.
    if (prochaine === undefined) return null
    // MÊME PIÈGE DE SUJET (2026-08-22) : « Personne avant demain » se lisait
    // « personne pour contrôler ». On nomme ce dont on parle.
    return { presse: false, liste: "aucune arrivée avant demain", ecran: null }
  }
  const m = /^(\d{1,2}):(\d{2})/.exec(String(prochaine.heure || ''))
  if (!m) return { presse: false, liste: "aucune arrivée avant demain", ecran: null }
  const arriveeMin = Number(m[1]) * 60 + Number(m[2])
  const prenom = prochaine.prenom || null
  const h = heureCourte(prochaine.heure)
  const reste = arriveeMin - nowMin
  // Le rôle ménage ne reçoit pas le voyageur : l'heure limite, rien d'autre
  // (maquette NEST v677 : « à contrôler avant 16h »).
  if (!prenom) {
    return { presse: true, dansHeures: Math.max(1, Math.floor(Math.max(reste, 0) / 60)),
      liste: `à contrôler avant ${h}`, ecran: `À contrôler avant ${h}.` }
  }
  if (reste <= 0) {
    return { presse: true, liste: `${prenom} est déjà là`, ecran: `${prenom} est déjà là.` }
  }
  const resteTexte = reste < 60 ? "moins d'une heure" : `${Math.floor(reste / 60)} h`
  return {
    presse: true,
    // Le bloc du bandeau large (« arrivée dans 2 h ») lit ce champ.
    dansHeures: Math.max(1, Math.floor(reste / 60)),
    liste: `${prenom} arrive à ${h} — ${resteTexte} pour reprendre`,
    ecran: `${prenom} arrive à ${h}. Il reste ${resteTexte} si quelque chose doit être repris.`,
  }
}

// ── « non cochée » PAR CATÉGORIE — correspondance de NOMS, jamais devinée. ───
// Les pièces de la checklist sont configurées par logement ; on les rapproche
// des 6 catégories par leur libellé normalisé (sans accents). Une ligne non
// cochée qui ne correspond à RIEN n'avertit RIEN (règle Dimitri : jamais un
// faux avertissement). Exporté pour les tests ET pour la future mesure de
// couverture par logement.
const MAPPING_CATEGORIES = [
  ['salle_de_bain', /(sdb|salle ?de ?bain|salle ?d.eau|douche|baignoire|lavabo|wc|toilette)/],
  ['cuisine', /(cuisine|evier|plaque|frigo|refrigerateur|micro.?onde|vaisselle|plan ?de ?travail|cafetiere)/],
  ['linge', /(linge|drap|serviette|housse|taie|literie)/],
  ['chambres', /(chambre|\blits?\b|matelas|oreiller)/],
  ['sols', /(\bsols?\b|aspirat|serpill?|balai)/],
]
export function mapperNonCochees(lignes) {
  const comptes = {}
  for (const l of lignes || []) {
    if (l.coche) continue
    const brut = String(l.label || l.id || '')
      .toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
    for (const [cat, re] of MAPPING_CATEGORIES) {
      if (re.test(brut)) { comptes[cat] = (comptes[cat] || 0) + 1; break }
    }
  }
  return comptes
}

/** « depuis 2 h » — l'âge d'une remontée qui attend (jamais de date brute). */
export function depuisTexte(iso) {
  if (!iso) return null
  const ms = Date.now() - new Date(iso).getTime()
  if (!Number.isFinite(ms) || ms < 0) return null
  const min = Math.floor(ms / 60000)
  if (min < 60) return `depuis ${Math.max(min, 1)} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `depuis ${h} h`
  return `depuis ${Math.floor(h / 24)} j`
}

function todayParisIso() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(new Date())
}
function nowMinParis() {
  const [h, m] = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date()).split(':').map(Number)
  return h * 60 + m
}

function buildLogLabel(cfg) {
  const byId = {}
  for (const prop of cfg?.props || []) {
    for (const log of prop.logs || []) {
      if (log?.id) byId[log.id] = log.nom_complet || log.nom || null
      for (const ch of log.chambres || []) {
        if (ch?.id) byId[ch.id] = ch.nom_complet || ch.nom || null
      }
    }
  }
  return (logementId) => byId[logementId] || null
}

// LE BANDEAU MAISON EST MORT (2026-08-21, règle du dessin unique) : l'écran
// utilise la carte de titre COMMUNE (CarteTitrePage) — la variante TERRACOTTA
// du signalement, le Retour et la barre de progression ont été AJOUTÉS à la
// carte commune plutôt que dupliqués ici.

// `destination` ({ runId }) : arrivée CIBLÉE depuis la frise du Suivi (bouton
// « Contrôler » orchestrateur) — consommée UNE FOIS puis signalée via
// onDestinationConsommee (même motif que l'écran Demandes). Run introuvable
// (déjà contrôlé entre-temps) → notice, jamais un écran muet.
// ── LE BLOC DES MANQUES — les PRODUITS en gras d'abord, l'agent en petit ────
export function libelleManques(manques, cfg) {
  return (manques || [])
    .map((m) => (m.cs_id ? nomProduit(cfg, m.cs_id) : m.texte))
    .filter(Boolean).join(', ')
}
export function signaleursDe(manques) {
  const noms = [...new Set((manques || []).map((m) => m.par_prenom).filter(Boolean))]
  if (!noms.length) return null
  return `signalé par ${noms.length > 1 ? noms.slice(0, -1).join(', ') + ' et ' + noms[noms.length - 1] : noms[0]}`
}
function BlocManques({ testId, fond, bord, couleur, titre, manques, cfg }) {
  const qui = signaleursDe(manques)
  return (
    <div data-testid={testId}
      style={{ background: fond, border: `1px solid ${bord}`, borderRadius: 12,
        padding: '11px 13px', marginBottom: 12 }}>
      <div style={{ fontSize: 13, color: couleur, lineHeight: 1.45 }}>
        {titre} : <strong>{libelleManques(manques, cfg)}</strong>
      </div>
      {qui && (
        <div style={{ fontSize: 10.5, color: '#98a2ad', marginTop: 3 }}>{qui}</div>
      )}
    </div>
  )
}

export default function ControleView({ basculePage = null, cfg, currentUser = null, destination = null, onDestinationConsommee }) {
  // ── LA LISTE ──────────────────────────────────────────────────────────────
  const [menages, setMenages] = useState(null)
  const [dejaServeur, setDejaServeur] = useState([])
  const [dejaLocal, setDejaLocal] = useState([])
  const [chargement, setChargement] = useState(true)
  const [erreurListe, setErreurListe] = useState(null)
  const [interdit, setInterdit] = useState(false)
  const [notice, setNotice] = useState(null)

  // Rechargement SILENCIEUX — après une désignation ou un événement de
  // synchro : pas de voile, la liste se remplace en place.
  const rechargerSilencieux = useCallback(async () => {
    try {
      const estPasse = jourVueRef.current < todayParisIso()
      const json = await fetchAControler(estPasse ? { date: jourVueRef.current } : {})
      setConsultation(!!json?.consultation)
      const liste = (Array.isArray(json?.menages) ? json.menages : [])
        .filter((m) => !m.controle_statut)
      liste.sort((a, b) => {
        if (a.completed_at && b.completed_at) return a.completed_at < b.completed_at ? -1 : 1
        if (a.completed_at) return -1
        if (b.completed_at) return 1
        return String(a.scheduled_date).localeCompare(String(b.scheduled_date))
      })
      setMenages(liste)
      setDejaServeur(Array.isArray(json?.deja_controles) ? json.deja_controles : [])
    } catch (_) { /* best-effort — la liste visible reste */ }
  }, [])

  // SYNCHRO (Dimitri 2026-08-21) : les trois chemins de désignation écrivent
  // la même chose — un changement fait AILLEURS (panneau Planning, frise) se
  // voit ICI immédiatement.
  useEffect(() => {
    window.addEventListener('fbz:controle-designation', rechargerSilencieux)
    return () => window.removeEventListener('fbz:controle-designation', rechargerSilencieux)
  }, [rechargerSilencieux])

  // LA DÉSIGNATION depuis le menu — 1 écriture (ce ménage), 2 si la case
  // « contrôleur habituel » est cochée (ménage + défaut du logement).
  const designerDepuisMenu = useCallback(async (menage, { email, parDefaut }) => {
    setNotice(null)
    const r1 = await designerControleurRun({ runId: menage.run_id, controleurEmail: email })
    if (!r1.ok) { setNotice({ ton: 'alerte', texte: r1.erreur }); return }
    if (parDefaut) {
      const r2 = await designerControleurLogement({ logementId: menage.logement_id, controleurEmail: email })
      if (!r2.ok) { setNotice({ ton: 'alerte', texte: r2.erreur }); return }
    }
    await rechargerSilencieux()
  }, [rechargerSilencieux])

  // NAVIGATION PAR JOUR (Dimitri 2026-08-21) — flèches comme sur Planning.
  // Aujourd'hui = la file vivante ; un jour PASSÉ = CONSULTATION (aucun
  // geste : « voir ce qui s'est passé, pas contrôler après coup »).
  const [jourVue, setJourVue] = useState(() => todayParisIso())
  const [consultation, setConsultation] = useState(false)
  const jourVueRef = useRef(jourVue)
  jourVueRef.current = jourVue

  const chargerListe = useCallback(async () => {
    setChargement(true); setErreurListe(null)
    try {
      const estPasse = jourVueRef.current < todayParisIso()
      const json = await fetchAControler(estPasse ? { date: jourVueRef.current } : {})
      setConsultation(!!json?.consultation)
      const liste = (Array.isArray(json?.menages) ? json.menages : [])
        .filter((m) => !m.controle_statut)
      // ORDRE CHRONOLOGIQUE DE FIN (maquette) : le serveur trie, le client
      // garantit — completed_at absent → en dernier, par date de ménage.
      liste.sort((a, b) => {
        if (a.completed_at && b.completed_at) return a.completed_at < b.completed_at ? -1 : 1
        if (a.completed_at) return -1
        if (b.completed_at) return 1
        return String(a.scheduled_date).localeCompare(String(b.scheduled_date))
      })
      setMenages(liste)
      setDejaServeur(Array.isArray(json?.deja_controles) ? json.deja_controles : [])
    } catch (e) {
      if (e.status === 403) { setInterdit(true) }
      else { setErreurListe(e.message || String(e)) }
    } finally {
      setChargement(false)
    }
  }, [])
  // C-a (tri Dimitri 2026-08-21) : UN SEUL chargement au montage — l'effet
  // « jour » ci-dessous tire aussi à l'arrivée, l'ancien double effet faisait
  // 2 appels (mesuré aux 5 contrôles).
  const naviguerJour = useCallback((pas) => {
    setJourVue((j) => {
      const d = new Date(Date.parse(`${j}T12:00:00Z`) + pas * 86400000)
      const prochain = d.toISOString().slice(0, 10)
      const auj = todayParisIso()
      return prochain > auj ? auj : prochain
    })
  }, [])
  useEffect(() => { chargerListe() }, [jourVue]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── LE NOM DES LOGEMENTS, EN CLAIR (guichet → cfg → neutre, jamais l'id). ──
  const [logInfos, setLogInfos] = useState({})
  useEffect(() => {
    const tous = [...(menages || []), ...dejaServeur]
    const cles = [...new Set(tous.map((m) => `${m.logement_id}:${m.scheduled_date}`))]
    if (!cles.length) { setLogInfos({}); return }
    let annule = false
    ;(async () => {
      const entrees = await Promise.all(cles.map(async (cle) => {
        const sep = cle.lastIndexOf(':')
        try {
          return [cle, await fetchLogInfo(cle.slice(0, sep), cle.slice(sep + 1))]
        } catch (e) {
          console.error('[controle] menage_log_info', e?.message || e)
          return null
        }
      }))
      if (!annule) setLogInfos(Object.fromEntries(entrees.filter(Boolean)))
    })()
    return () => { annule = true }
  }, [menages, dejaServeur])

  const labelCfg = useMemo(() => buildLogLabel(cfg), [cfg])
  const nomDe = useCallback((m) => (
    logInfos[`${m.logement_id}:${m.scheduled_date}`]?.nom || labelCfg(m.logement_id) || 'Logement'
  ), [logInfos, labelCfg])

  // ── L'ÉCRAN OUVERT ────────────────────────────────────────────────────────
  const [detail, setDetail] = useState(null) // { menage, checklist, chargement, erreur }
  // LA BASCULE (maquette Dimitri 2026-08-21) : « À contrôler » (l'outil de
  // terrain, INCHANGÉ) / « Le bilan » (la vue neuve). Ouvre TOUJOURS sur
  // « À contrôler », JAMAIS mémorisé — et réservée au pilotage
  // (orchestrateur / admin) : l'agent-contrôleur terrain n'a pas le bilan.
  const [vue, setVue] = useState('a-controler')
  const roleVue = currentUser?.role || null
  const avecBilan = roleVue === 'orchestrateur' || roleVue === 'admin'

  const estLarge = useLargeEcran()
  // Les agents désignables (pool + prénoms saisis) — cache 5 min, {} en erreur.
  const [prenomsPool, setPrenomsPool] = useState({})
  useEffect(() => {
    if (!avecBilan) return
    let vivant = true
    fetchPoolPrenoms().then((p) => { if (vivant) setPrenomsPool(p || {}) })
    return () => { vivant = false }
  }, [avecBilan])

  const [sousEcran, setSousEcran] = useState('controle') // controle|checklist|categories|point|recap
  const [envoiEnCours, setEnvoiEnCours] = useState(false)
  const [erreurEnvoi, setErreurEnvoi] = useState(null)
  const [derniereTentative, setDerniereTentative] = useState(null)

  // ── LE WIZARD DU SIGNALEMENT ──────────────────────────────────────────────
  const [catsChoisies, setCatsChoisies] = useState([])
  const [idxPoint, setIdxPoint] = useState(0)
  const [saisies, setSaisies] = useState({}) // { [catId]: { texte, photos: [...] } }
  const [depuisRecap, setDepuisRecap] = useState(false)
  const [repriseChoix, setRepriseChoix] = useState(null) // 'controleur'|'agent'
  const seqRef = useRef(0)

  const retirerDeLaListe = useCallback((runId) => {
    setMenages((prev) => (prev || []).filter((m) => m.run_id !== runId))
  }, [])

  const viderWizard = useCallback(() => {
    setSaisies((prev) => {
      for (const s of Object.values(prev)) for (const p of s.photos || []) libererApercu(p.apercu)
      return {}
    })
    setCatsChoisies([]); setIdxPoint(0); setDepuisRecap(false); setRepriseChoix(null)
  }, [])

  const revenirListe = useCallback(() => {
    setDetail(null); setSousEcran('controle'); setErreurEnvoi(null)
    viderWizard()
  }, [viderWizard])

  async function chargerChecklist(menage) {
    try {
      const checklist = await fetchRunChecklist(menage.run_id)
      setDetail((prev) => (prev && prev.menage.run_id === menage.run_id
        ? { ...prev, checklist, chargement: false } : prev))
    } catch (e) {
      if (e.status === 409 || e.status === 410) {
        setNotice({
          ton: 'alerte',
          texte: e.status === 409
            ? 'Ce ménage a déjà été contrôlé.'
            : 'La fenêtre de contrôle est passée (heure du contrat + 12 h).',
        })
        retirerDeLaListe(menage.run_id)
        setDetail(null)
        return
      }
      setDetail((prev) => (prev && prev.menage.run_id === menage.run_id
        ? { ...prev, chargement: false, erreur: e.message || String(e) } : prev))
    }
  }

  // Consommation de la destination — quand la liste est là, pas avant.
  const destConsommee = useRef(false)
  useEffect(() => {
    if (!destination?.runId || destConsommee.current || menages === null) return
    destConsommee.current = true
    const cible = (menages || []).find((m) => m.run_id === destination.runId)
    if (cible) {
      ouvrirControle(cible)
    } else {
      // RE-CONTRÔLE (option B — correction du 2026-08-22) : un ménage déjà
      // contrôlé ne vit plus dans « à contrôler », c'est PRÉCISÉMENT lui que
      // « Refaire un contrôle » envoie ici. On le rouvre depuis les
      // déjà-contrôlés : le dépôt AJOUTERA une ligne (append-only, le serveur
      // ne bloque qu'un contrôle OUVERT), l'ancienne reste dans l'historique.
      const deja = (dejaServeur || []).find((d) => d.run_id === destination.runId)
      if (deja && !consultation) {
        ouvrirControle(deja)
      } else {
        setNotice({ ton: 'alerte', texte: 'Ce ménage n’est plus contrôlable (fenêtre passée).' })
      }
    }
    if (onDestinationConsommee) onDestinationConsommee()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination, menages, dejaServeur])

  const ouvrirControle = useCallback(async (menage) => {
    setNotice(null); setSousEcran('controle')
    setDetail({ menage, checklist: null, chargement: true, erreur: null })
    await chargerChecklist(menage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── L'ENVOI EFFECTIF ──────────────────────────────────────────────────────
  const marquerControle = useCallback((menage, statut, extras = {}) => {
    retirerDeLaListe(menage.run_id)
    setDejaLocal((prev) => [{
      run_id: menage.run_id, logement_id: menage.logement_id,
      scheduled_date: menage.scheduled_date, agent_prenom: menage.agent_prenom,
      statut, controle_le: new Date().toISOString(), ...extras,
    }, ...prev])
  }, [retirerDeLaListe])

  const envoyerVerdict = useCallback(async (menage, verdict, pts, reprisePar) => {
    setEnvoiEnCours(true); setErreurEnvoi(null)
    setDerniereTentative({ menage, verdict, points: pts, reprisePar })
    try {
      const json = await envoyerControle({ runId: menage.run_id, verdict, points: pts, reprisePar })
      // ⚠️ GARANTIE TRANSFORMÉE (Dimitri 2026-08-19) : « je reprends
      // maintenant » naît CLOS — il est sur place, il corrige dans la foulée,
      // plus de « C'est réglé » après coup. « Karim reprendra » reste en
      // attente : c'est Karim qui clôt en cochant ses points.
      marquerControle(menage,
        verdict === 'bon' ? 'bon'
          : reprisePar === 'controleur' ? 'regle' : 'reprise_en_cours', {
          reprise_par: reprisePar || null,
          nb_points: pts.length,
          controle_id: json?.controle?.id || null,
        })
      setNotice({
        ton: 'ok',
        texte: verdict === 'bon'
          ? 'Contrôle enregistré : rien à signaler.'
          : `Contrôle envoyé : ${pts.length} point${pts.length > 1 ? 's' : ''} signalé${pts.length > 1 ? 's' : ''}.`,
      })
      // C-a (2026-08-21) : la pastille du menu se met à jour TOUT DE SUITE.
      window.dispatchEvent(new Event('fbz:orchestrateur-badges'))
      revenirListe()
      return true
    } catch (e) {
      if (e.status === 409 || e.status === 410) {
        setNotice({
          ton: 'alerte',
          texte: e.status === 409
            ? 'Ce ménage a déjà été contrôlé.'
            : 'La fenêtre de contrôle est passée (heure du contrat + 12 h).',
        })
        retirerDeLaListe(menage.run_id)
        revenirListe()
        return false
      }
      setErreurEnvoi(e.message || String(e))
      return false
    } finally {
      setEnvoiEnCours(false)
    }
  }, [marquerControle, retirerDeLaListe, revenirListe])

  // (« C'est réglé » supprimé le 2026-08-19 : « je reprends maintenant »
  // naît clos côté serveur — plus rien à valider après coup.)

  // ── « RIEN À SIGNALER » — L'ANNULATION DIFFÉRÉE ───────────────────────────
  // Le POST part APRÈS le délai. Les timers meurent avec le composant :
  // application fermée pendant le délai ⇒ rien d'envoyé (décision Dimitri).
  const [differes, setDiferes] = useState({}) // { [runId]: menage }
  const timersRef = useRef({})
  useEffect(() => () => {
    for (const t of Object.values(timersRef.current)) clearTimeout(t)
  }, [])

  const lancerRienASignaler = useCallback((menage) => {
    setNotice(null)
    setDiferes((prev) => ({ ...prev, [menage.run_id]: menage }))
    timersRef.current[menage.run_id] = setTimeout(async () => {
      delete timersRef.current[menage.run_id]
      setDiferes((prev) => {
        const { [menage.run_id]: _, ...reste } = prev
        return reste
      })
      try {
        await envoyerControle({ runId: menage.run_id, verdict: 'bon', points: [] })
        marquerControle(menage, 'bon', { nb_points: 0 })
        window.dispatchEvent(new Event('fbz:orchestrateur-badges'))
        setNotice({ ton: 'ok', texte: 'Contrôle enregistré : rien à signaler.' })
      } catch (e) {
        if (e.status === 409 || e.status === 410) {
          setNotice({
            ton: 'alerte',
            texte: e.status === 409
              ? 'Ce ménage a déjà été contrôlé.'
              : 'La fenêtre de contrôle est passée (heure du contrat + 12 h).',
          })
          retirerDeLaListe(menage.run_id)
        } else {
          // Le ménage RESTE dans la liste — l'échec se dit, rien n'est perdu.
          setNotice({ ton: 'alerte', texte: 'L’envoi de « Rien à signaler » a échoué — réessayez.' })
        }
      }
    }, DELAI_ANNULATION_MS)
  }, [marquerControle, retirerDeLaListe])

  const annulerDiffere = useCallback((runId) => {
    if (timersRef.current[runId]) { clearTimeout(timersRef.current[runId]); delete timersRef.current[runId] }
    setDiferes((prev) => {
      const { [runId]: _, ...reste } = prev
      return reste
    })
  }, [])

  // Depuis l'ÉCRAN de contrôle : même geste différé, puis retour liste (la
  // carte verte annulable y est visible).
  const rienASignalerDepuisEcran = useCallback((menage) => {
    lancerRienASignaler(menage)
    revenirListe()
  }, [lancerRienASignaler, revenirListe])

  // ── LES PHOTOS D'UN POINT (par catégorie) ─────────────────────────────────
  const majPhoto = useCallback((catId, phCle, patch) => {
    setSaisies((prev) => ({
      ...prev,
      [catId]: {
        ...(prev[catId] || { texte: '', photos: [] }),
        photos: ((prev[catId] || {}).photos || []).map((p) => (p.cle === phCle ? { ...p, ...patch } : p)),
      },
    }))
  }, [])

  const lancerUploadPhoto = useCallback(async (menage, catId, phCle, file) => {
    try {
      let blob = file
      let mime = file.type || 'image/jpeg'
      try {
        const compresse = await compressImage(file, { targetBytes: PHOTO_CIBLE_OCTETS })
        if (compresse) { blob = compresse.blob || compresse; mime = (compresse.blob || compresse).type || mime }
      } catch (e) { console.error('[controle] compression', e?.message || e) }
      const dataBase64 = await fichierEnBase64(blob)
      const json = await uploaderPhotoControle({ runId: menage.run_id, mime, dataBase64 })
      majPhoto(catId, phCle, { statut: 'ok', path: json.path })
    } catch (e) {
      console.error('[controle] upload photo', e?.message || e)
      majPhoto(catId, phCle, { statut: 'echec', erreur: e.message || 'Envoi impossible. Réessayez.' })
    }
  }, [majPhoto])

  const ajouterPhoto = useCallback((menage, catId, file) => {
    if (!file) return
    const phCle = `ph${++seqRef.current}`
    let apercu = null
    try {
      if (typeof URL !== 'undefined' && URL.createObjectURL) apercu = URL.createObjectURL(file)
    } catch (e) { console.error('[controle] aperçu photo', e?.message || e) }
    setSaisies((prev) => ({
      ...prev,
      [catId]: {
        ...(prev[catId] || { texte: '', photos: [] }),
        photos: [...((prev[catId] || {}).photos || []),
          { cle: phCle, apercu, statut: 'envoi', path: null, erreur: null, file }],
      },
    }))
    lancerUploadPhoto(menage, catId, phCle, file)
  }, [lancerUploadPhoto])

  const supprimerPhoto = useCallback((catId, phCle) => {
    setSaisies((prev) => ({
      ...prev,
      [catId]: {
        ...(prev[catId] || { texte: '', photos: [] }),
        photos: ((prev[catId] || {}).photos || []).filter((p) => {
          if (p.cle !== phCle) return true
          libererApercu(p.apercu)
          return false
        }),
      },
    }))
  }, [])

  // ── RENDU ─────────────────────────────────────────────────────────────────
  if (interdit) return null

  if (chargement && menages === null) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: DS.textMuted,
        fontSize: 14, fontFamily: DS.fontBody }}>Chargement…</div>
    )
  }

  if (erreurListe && menages === null) {
    return (
      <div style={{ padding: 24, fontFamily: DS.fontBody }}>
        <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: 14,
          background: DS.dangerBg, color: DS.dangerText, borderRadius: 10,
          fontSize: 13, lineHeight: 1.5 }}>
          <CircleAlert size={17} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
          {erreurListe}
        </div>
        <button onClick={chargerListe} className="fbz-press fbz-tap"
          style={{ marginTop: 14, display: 'inline-flex', alignItems: 'center', gap: 8,
            background: C.navy, color: '#fff', border: 'none',
            borderRadius: DS.radiusControl, padding: '11px 18px', fontSize: 13,
            fontWeight: 700, cursor: 'pointer', fontFamily: DS.fontBody }}>
          <RefreshCw size={16} strokeWidth={2} /> Réessayer
        </button>
      </div>
    )
  }

  // PLEINE LARGEUR (2026-08-21) : plus de plafond local — bandeau à la
  // largeur commune, comme Planning / Demandes / Incidents.
  const conteneur = { padding: '0 0 80px',
    fontFamily: DS.fontBody, color: C.fonce }
  const today = todayParisIso()
  const nowMin = nowMinParis()

  // ═══ LE SIGNALEMENT (wizard) ═══
  if (detail && (sousEcran === 'categories' || sousEcran === 'point' || sousEcran === 'recap')) {
    const { menage, checklist } = detail
    const lignes = checklist ? lignesChecklist(checklist.items_checked) : []
    const avertissements = mapperNonCochees(lignes)

    // ── Écran CATÉGORIES ──
    if (sousEcran === 'categories') {
      const n = catsChoisies.length
      return (
        <div style={conteneur} data-testid="controle-ecran-categories">
          <CarteTitrePage fond={TERRA_DEGRADE} titre="Des points à reprendre"
            sousTitre={nomDe(menage)} testId="controle-bandeau-signalement"
            retourTestId="controle-retour"
            onRetour={() => setSousEcran('controle')} />
          <div style={{ fontSize: 12, color: DS.textSecondary, margin: '0 2px 10px' }}>
            Où y a-t-il quelque chose à reprendre ?
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {CATEGORIES_POINTS.map((cat) => {
              const actif = catsChoisies.includes(cat.id)
              const avert = avertissements[cat.id] || 0
              const Icone = cat.icone
              return (
                <button key={cat.id} type="button" data-testid={`controle-cat-${cat.id}`}
                  aria-pressed={actif}
                  onClick={() => setCatsChoisies((prev) => (actif
                    ? prev.filter((c) => c !== cat.id)
                    : CATEGORIES_POINTS.map((c) => c.id).filter((c) => prev.includes(c) || c === cat.id)))}
                  className="fbz-press fbz-tap"
                  style={{ minHeight: CIBLE_VERDICT, display: 'flex', alignItems: 'center',
                    gap: 11, padding: '0 13px', borderRadius: 12, textAlign: 'left',
                    border: `1.5px solid ${actif ? TERRA : C.bordFort}`,
                    background: actif ? TERRA_FOND : '#fff', cursor: 'pointer',
                    fontFamily: DS.fontBody }}>
                  <Icone size={18} strokeWidth={2} color={actif ? TERRA : C.navy}
                    style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 13.5, fontWeight: 700,
                    color: actif ? TERRA_SOMBRE : C.texte }}>
                    {cat.label}
                  </span>
                  {avert > 0 && (
                    <span data-testid={`controle-avert-${cat.id}`}
                      style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 700,
                        background: AMBRE.fond, color: AMBRE.texte,
                        border: `1px solid ${AMBRE.bordure}`, borderRadius: 7,
                        padding: '3px 8px' }}>
                      {avert > 1 ? `${avert} non cochées` : 'non cochée'}
                    </span>
                  )}
                  {actif && <CircleCheck size={17} strokeWidth={2.2} color={TERRA}
                    style={{ flexShrink: 0 }} />}
                </button>
              )
            })}
          </div>
          <button type="button" data-testid="controle-continuer"
            disabled={n === 0}
            onClick={() => { setIdxPoint(0); setDepuisRecap(false); setSousEcran('point') }}
            className="fbz-press fbz-tap"
            style={{ marginTop: 16, width: '100%', minHeight: CIBLE_VERDICT,
              background: n > 0 ? TERRA : C.filet,
              color: n > 0 ? '#fff' : DS.textMuted, border: 'none', borderRadius: 12,
              fontSize: 14.5, fontWeight: 700, fontFamily: DS.fontBody,
              cursor: n > 0 ? 'pointer' : 'default' }}>
            {n > 0 ? `Continuer · ${n} point${n > 1 ? 's' : ''}` : 'Continuer'}
          </button>
        </div>
      )
    }

    // ── Écran UN POINT À LA FOIS ──
    if (sousEcran === 'point') {
      const catId = catsChoisies[idxPoint]
      const cat = CATEGORIES_POINTS.find((c) => c.id === catId) || { label: humaniserId(catId) }
      const saisie = saisies[catId] || { texte: '', photos: [] }
      const dernier = idxPoint >= catsChoisies.length - 1
      const suivante = dernier ? null
        : (CATEGORIES_POINTS.find((c) => c.id === catsChoisies[idxPoint + 1]) || {}).label
      const photos = saisie.photos || []
      const plafondAtteint = photos.length >= MAX_PHOTOS_PAR_POINT
      const pret = !!saisie.texte.trim()
      return (
        <div style={conteneur} data-testid="controle-ecran-point">
          <CarteTitrePage fond={TERRA_DEGRADE} titre={cat.label}
            sousTitre={`point ${idxPoint + 1} sur ${catsChoisies.length}`}
            progression={(idxPoint + 1) / catsChoisies.length}
            progressionTestId="controle-progression"
            testId="controle-bandeau-point"
            retourTestId="controle-retour"
            onRetour={() => {
              if (depuisRecap) { setSousEcran('recap'); setDepuisRecap(false) }
              else if (idxPoint > 0) setIdxPoint(idxPoint - 1)
              else setSousEcran('categories')
            }} />

          <label style={{ display: 'block', fontSize: 11, fontWeight: 700,
            color: DS.textSecondary, textTransform: 'uppercase', letterSpacing: '.04em',
            margin: '2px 0 7px' }} htmlFor="controle-point-texte">
            Ce qui est à reprendre
          </label>
          <textarea id="controle-point-texte" data-testid="controle-point-texte"
            value={saisie.texte} autoFocus rows={4}
            onChange={(e) => setSaisies((prev) => ({
              ...prev,
              [catId]: { ...(prev[catId] || { photos: [] }), texte: e.target.value },
            }))}
            placeholder="Ex. : poussière sous le lit, trace sur le miroir…"
            style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${C.bordFort}`,
              borderRadius: 11, padding: 13, fontSize: 15, fontFamily: DS.fontBody,
              color: C.texte, resize: 'vertical' }} />

          {photos.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 11 }}>
              {photos.map((p, j) => (
                <div key={p.cle} data-testid={`controle-photo-point-${idxPoint}-${j}`}
                  data-statut={p.statut}
                  style={{ position: 'relative', width: 76 }}>
                  <img src={p.apercu || undefined} alt={`Photo ${j + 1}`}
                    style={{ width: 76, height: 76, objectFit: 'cover', borderRadius: 9,
                      display: 'block',
                      border: `2px solid ${p.statut === 'echec' ? DS.dangerText : C.bord}`,
                      opacity: p.statut === 'envoi' ? .55 : 1 }} />
                  {p.statut === 'envoi' && (
                    <span style={{ position: 'absolute', left: 0, right: 0, bottom: 4,
                      textAlign: 'center', fontSize: 10.5, fontWeight: 700, color: C.navy }}>
                      Envoi…
                    </span>
                  )}
                  <button type="button" data-testid={`controle-photo-supprimer-${idxPoint}-${j}`}
                    aria-label={`Retirer la photo ${j + 1}`}
                    onClick={() => supprimerPhoto(catId, p.cle)} className="fbz-press fbz-tap"
                    style={{ position: 'absolute', top: -7, right: -7, width: 24, height: 24,
                      borderRadius: 12, background: C.fonce, color: '#fff',
                      border: '2px solid #fff', display: 'inline-flex', alignItems: 'center',
                      justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
                    <X size={12} strokeWidth={2.6} />
                  </button>
                </div>
              ))}
            </div>
          )}
          {photos.filter((p) => p.statut === 'echec').map((p) => (
            <div key={`err-${p.cle}`} style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, color: DS.dangerText, lineHeight: 1.4 }}>
                {p.erreur || 'Photo non envoyée.'}
              </div>
              <button type="button"
                data-testid={`controle-photo-reessayer-${idxPoint}-${photos.indexOf(p)}`}
                onClick={() => { majPhoto(catId, p.cle, { statut: 'envoi', erreur: null }); lancerUploadPhoto(menage, catId, p.cle, p.file) }}
                className="fbz-press fbz-tap"
                style={{ marginTop: 5, display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: C.navy, color: '#fff', border: 'none', borderRadius: 8,
                  padding: '8px 13px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  fontFamily: DS.fontBody }}>
                <RefreshCw size={13} strokeWidth={2.2} /> Réessayer la photo
              </button>
            </div>
          ))}

          <input type="file" accept="image/*" capture="environment"
            data-testid="controle-photo-input" id="controle-photo-input"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0]
              e.target.value = ''
              if (file && !plafondAtteint) ajouterPhoto(menage, catId, file)
            }} />
          <button type="button" data-testid="controle-ajouter-photo"
            disabled={plafondAtteint}
            onClick={() => {
              const input = document.getElementById('controle-photo-input')
              if (input) input.click()
            }}
            className="fbz-press fbz-tap"
            style={{ marginTop: 11, width: '100%', minHeight: CIBLE,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              background: '#fff', border: `1px dashed ${plafondAtteint ? C.bord : C.bordFort}`,
              borderRadius: 11, color: plafondAtteint ? DS.textMuted : C.navy,
              fontSize: 12.5, fontWeight: 700, fontFamily: DS.fontBody,
              cursor: plafondAtteint ? 'default' : 'pointer' }}>
            <Camera size={16} strokeWidth={2.2} />
            {plafondAtteint ? `${MAX_PHOTOS_PAR_POINT} photos maximum par point` : 'Ajouter une photo'}
          </button>

          <button type="button" data-testid="controle-point-suivant"
            disabled={!pret}
            onClick={() => {
              if (depuisRecap) { setSousEcran('recap'); setDepuisRecap(false) }
              else if (dernier) setSousEcran('recap')
              else setIdxPoint(idxPoint + 1)
            }}
            className="fbz-press fbz-tap"
            style={{ marginTop: 16, width: '100%', minHeight: CIBLE_VERDICT,
              background: pret ? TERRA : C.filet, color: pret ? '#fff' : DS.textMuted,
              border: 'none', borderRadius: 12, fontSize: 14.5, fontWeight: 700,
              fontFamily: DS.fontBody, cursor: pret ? 'pointer' : 'default' }}>
            {depuisRecap ? 'Revenir au récapitulatif'
              : dernier ? 'Récapitulatif' : `Point suivant · ${suivante}`}
          </button>
        </div>
      )
    }

    // ── Écran RÉCAPITULATIF ──
    const pts = catsChoisies.map((catId) => ({
      categorie: catId,
      texte: (saisies[catId]?.texte || '').trim(),
      photos: saisies[catId]?.photos || [],
    }))
    const photosEnVol = pts.some((pt) => pt.photos.some((p) => p.statut === 'envoi'))
    const photosEnEchec = pts.some((pt) => pt.photos.some((p) => p.statut === 'echec'))
    const peutEnvoyer = pts.length > 0 && pts.every((pt) => pt.texte)
      && !!repriseChoix && !envoiEnCours && !photosEnVol && !photosEnEchec
    const prenomAgent = menage.agent_prenom || "L'agent"
    return (
      <div style={conteneur} data-testid="controle-ecran-recap">
        <CarteTitrePage fond={TERRA_DEGRADE} titre="Récapitulatif"
          sousTitre={`${pts.length} point${pts.length > 1 ? 's' : ''} · ${nomDe(menage)}`}
          testId="controle-bandeau-recap"
          retourTestId="controle-retour"
          onRetour={() => { setIdxPoint(catsChoisies.length - 1); setSousEcran('point') }} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {pts.map((pt, i) => {
            const cat = CATEGORIES_POINTS.find((c) => c.id === pt.categorie) || {}
            return (
              <div key={pt.categorie} data-testid={`controle-recap-point-${i}`}
                style={{ background: '#fff', border: `1px solid ${C.bord}`, borderRadius: 12,
                  padding: '11px 12px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: TERRA }}>{cat.label}</div>
                  <div style={{ fontSize: 13, color: C.texte, lineHeight: 1.4, marginTop: 2 }}>
                    {pt.texte || <em style={{ color: DS.dangerText }}>texte manquant</em>}
                  </div>
                  {pt.photos.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                      {pt.photos.map((p, j) => (
                        <img key={p.cle} src={p.apercu || undefined} alt={`Photo ${j + 1}`}
                          style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 7,
                            border: `1.5px solid ${p.statut === 'echec' ? DS.dangerText : C.bord}`,
                            opacity: p.statut === 'envoi' ? .55 : 1 }} />
                      ))}
                    </div>
                  )}
                </div>
                <button type="button" data-testid={`controle-recap-crayon-${i}`}
                  aria-label={`Corriger le point ${cat.label}`}
                  onClick={() => { setIdxPoint(i); setDepuisRecap(true); setSousEcran('point') }}
                  className="fbz-press fbz-tap"
                  style={{ width: 38, height: 38, flexShrink: 0, borderRadius: 9,
                    background: C.filet, border: 'none', display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center', color: C.navy,
                    cursor: 'pointer' }}>
                  <Pencil size={15} strokeWidth={2.2} />
                </button>
              </div>
            )
          })}
        </div>

        {/* « Qui reprend ? » — AU DERNIER MOMENT, quand il sait ce qu'il y a à
            faire. Deux chemins, l'agent est prévenu dans les deux cas (jamais
            de l'identité du contrôleur — anonymat serveur). */}
        <div style={{ fontSize: 11, fontWeight: 700, color: DS.textSecondary,
          textTransform: 'uppercase', letterSpacing: '.04em', margin: '18px 2px 8px' }}>
          Qui reprend ?
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { id: 'controleur', titre: 'Je reprends maintenant', sous: 'je suis sur place' },
            { id: 'agent', titre: `${prenomAgent} reprendra`, sous: 'les points lui seront transmis' },
          ].map((opt) => {
            const actif = repriseChoix === opt.id
            return (
              <button key={opt.id} type="button" data-testid={`controle-reprise-${opt.id}`}
                aria-pressed={actif} onClick={() => setRepriseChoix(opt.id)}
                className="fbz-press fbz-tap"
                style={{ minHeight: CIBLE_VERDICT, display: 'flex', alignItems: 'center',
                  gap: 10, padding: '9px 13px', borderRadius: 12, textAlign: 'left',
                  border: `1.5px solid ${actif ? TERRA : C.bordFort}`,
                  background: actif ? TERRA_FOND : '#fff', cursor: 'pointer',
                  fontFamily: DS.fontBody }}>
                {actif
                  ? <CircleCheck size={18} strokeWidth={2.2} color={TERRA} style={{ flexShrink: 0 }} />
                  : <Circle size={17} strokeWidth={2} color={C.bordFort} style={{ flexShrink: 0 }} />}
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700,
                    color: actif ? TERRA_SOMBRE : C.texte }}>
                    {opt.titre}
                  </span>
                  <span style={{ display: 'block', fontSize: 11.5, color: DS.textSecondary }}>
                    {opt.sous}
                  </span>
                </span>
              </button>
            )
          })}
        </div>

        {erreurEnvoi && (
          <BandeauErreurEnvoi texte={erreurEnvoi}
            onRetry={() => envoyerVerdict(menage, 'a_reprendre',
              derniereTentative?.points || construirePayloadPoints(pts),
              derniereTentative?.reprisePar || repriseChoix)} />
        )}
        {(photosEnVol || photosEnEchec) && (
          <div data-testid="controle-envoi-bloque-photos"
            style={{ marginTop: 14, display: 'flex', gap: 8, alignItems: 'flex-start',
              padding: '10px 13px', borderRadius: 10, fontSize: 12.5, fontWeight: 600,
              background: DS.warningBg, color: DS.warningText, lineHeight: 1.45 }}>
            <CircleAlert size={15} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} />
            {photosEnVol
              ? 'Des photos sont encore en cours d’envoi — patientez un instant.'
              : 'Une photo n’est pas partie : réessayez-la ou retirez-la avant d’envoyer.'}
          </div>
        )}

        <button type="button" data-testid="controle-envoyer"
          disabled={!peutEnvoyer}
          onClick={() => envoyerVerdict(menage, 'a_reprendre', construirePayloadPoints(pts), repriseChoix)}
          className="fbz-press fbz-tap"
          style={{ marginTop: 16, width: '100%', minHeight: CIBLE_VERDICT,
            background: peutEnvoyer ? TERRA : C.filet,
            color: peutEnvoyer ? '#fff' : DS.textMuted, border: 'none', borderRadius: 12,
            fontSize: 14.5, fontWeight: 700, fontFamily: DS.fontBody,
            cursor: peutEnvoyer ? 'pointer' : 'default' }}>
          {envoiEnCours ? 'Envoi…' : `Envoyer ${pts.length > 1 ? `les ${pts.length} points` : 'le point'}`}
        </button>
      </div>
    )
  }

  // ═══ LA CHECKLIST (lecture seule — AUCUN verdict ici) ═══
  if (detail && sousEcran === 'checklist') {
    const { menage, checklist } = detail
    const lignes = checklist ? lignesChecklist(checklist.items_checked) : []
    const nonCochees = lignes.filter((l) => !l.coche).length
    return (
      <div style={conteneur} data-testid="controle-ecran-checklist">
        <CarteTitrePage titre={nomDe(menage)}
          sousTitre={checklist?.agent_prenom ? `checklist de ${checklist.agent_prenom}` : 'checklist'}
          retourTestId="controle-retour"
          onRetour={() => setSousEcran('controle')}
          actions={
            <span data-testid="controle-badge-lecture"
              style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5,
                background: C.navy, color: '#fff', borderRadius: 7, padding: '6px 11px',
                fontSize: 11.5, fontWeight: 700, border: '1px solid rgba(255,255,255,.4)' }}>
              <Eye size={13} strokeWidth={2.2} /> Lecture
            </span>
          } />

        <div style={{ background: '#fff', border: `1px solid ${C.bord}`, borderRadius: 12,
          overflow: 'hidden' }}>
          <div style={{ padding: '11px 14px', borderBottom: `1px solid ${C.filet}`,
            fontSize: 12, color: DS.textSecondary }}>
            {lignes.length === 0
              ? 'Aucune case renseignée sur ce ménage.'
              : nonCochees > 0
                ? `${nonCochees} ligne${nonCochees > 1 ? 's' : ''} non cochée${nonCochees > 1 ? 's' : ''} — à regarder en premier.`
                : 'Toutes les cases sont cochées.'}
          </div>
          {lignes.map((l) => (
            <div key={l.id} data-testid={`controle-ligne-${l.id}`}
              data-cochee={l.coche ? 'oui' : 'non'}
              style={l.coche
                ? { display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px',
                    borderBottom: `1px solid ${C.filet}`, fontSize: 13.5, color: C.texte }
                : { display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px',
                    borderBottom: `1px solid ${C.filet}`, fontSize: 13.5,
                    background: AMBRE.fond, borderLeft: `3px solid ${AMBRE.bordure}`,
                    color: AMBRE.texte, fontWeight: 600 }}>
              {l.coche
                ? <Check size={16} strokeWidth={2.4} color={VERT} style={{ flexShrink: 0 }} />
                : <Circle size={15} strokeWidth={2} color={AMBRE.bordure} style={{ flexShrink: 0 }} />}
              <span style={{ flex: 1, minWidth: 0 }}>{l.label}</span>
              {!l.coche && (
                <span style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  Non coché
                </span>
              )}
            </div>
          ))}
        </div>

        {Array.isArray(checklist?.photos) && checklist.photos.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: DS.textSecondary,
              textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>
              Photos du ménage ({checklist.photos.length})
            </div>
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
              {checklist.photos.map((p, i) => (
                <a key={`${p.url}-${i}`} href={p.url} target="_blank" rel="noopener noreferrer"
                  style={{ flexShrink: 0 }}>
                  <img src={p.url} alt={`Photo du ménage ${i + 1}`}
                    data-testid={`controle-photo-${i}`}
                    style={{ width: 88, height: 88, objectFit: 'cover', borderRadius: 9,
                      border: `1px solid ${C.bord}`, display: 'block' }} />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  // ═══ L'ÉCRAN DE CONTRÔLE (au clic « Ouvrir ») ═══
  if (detail) {
    const { menage, checklist } = detail
    const lignes = checklist ? lignesChecklist(checklist.items_checked) : []
    const nonCochees = lignes.filter((l) => !l.coche).length
    // Nb de lignes CONFIGURÉES sur le logement (serveur). Repli sur les lignes
    // renseignées : un vieux payload sans le champ ne doit pas transformer
    // une checklist remplie en « aucune checklist ».
    const configurees = Number.isFinite(checklist?.lignes_configurees)
      ? checklist.lignes_configurees
      : lignes.length
    const finiA = heureFR(menage.completed_at)
    const urg = urgenceDe(menage.prochaine_arrivee, nowMin, today, menage.heure_contrat, menage.scheduled_date)
    const prenomAgent = checklist?.agent_prenom || menage.agent_prenom || "l'agent"
    return (
      <div style={conteneur} data-testid="controle-ecran-detail">
        <CarteTitrePage titre={nomDe(menage)}
          sousTitre={[dateCourteControle(menage.scheduled_date),
            menage.agent_prenom ? `ménage de ${menage.agent_prenom}` : null,
            finiA ? `fini à ${finiA}` : null].filter(Boolean).join(' · ')}
          retourTestId="controle-retour"
          onRetour={revenirListe} />

        {/* a) CE QUI PRESSE, en tête — trois points quand le voyageur arrive
            dans deux heures, ce n'est pas le même geste que sur un logement
            vide jusqu'à demain. */}
        {urg && urg.presse && urg.ecran && (
          <div data-testid="controle-urgence"
            style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '12px 14px',
              background: TERRA_FOND, border: `1px solid ${TERRA_BORD}`, borderRadius: 12,
              marginBottom: 12, fontSize: 13, fontWeight: 600, color: TERRA_SOMBRE,
              lineHeight: 1.45 }}>
            <CircleAlert size={16} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} />
            {urg.ecran}
          </div>
        )}

        {detail.chargement && (
          <div style={{ padding: 24, textAlign: 'center', color: DS.textMuted, fontSize: 13.5 }}>
            Chargement de la checklist…
          </div>
        )}
        {detail.erreur && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: 14,
              background: DS.dangerBg, color: DS.dangerText, borderRadius: 10,
              fontSize: 13, lineHeight: 1.5 }}>
              <CircleAlert size={17} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
              {detail.erreur}
            </div>
            <button onClick={() => { setDetail({ menage, checklist: null, chargement: true, erreur: null }); chargerChecklist(menage) }}
              className="fbz-press fbz-tap"
              style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 8,
                background: C.navy, color: '#fff', border: 'none',
                borderRadius: DS.radiusControl, padding: '11px 18px', fontSize: 13,
                fontWeight: 700, cursor: 'pointer', fontFamily: DS.fontBody }}>
              <RefreshCw size={16} strokeWidth={2} /> Réessayer
            </button>
          </div>
        )}

        {/* LES MANQUES SIGNALÉS (2026-08-21) — il est SUR PLACE, c'est lui
            qui peut corriger. « Aujourd'hui » en TERRACOTTA avant les
            verdicts ; « prochaine fois » du ménage PRÉCÉDENT en ambre. */}
        {/* Recalage 2026-08-21 : DIRE, pas raconter — LES PRODUITS d'abord
            (gras, un seul bloc), l'agent en petit gris dessous. */}
        {(menage.manques_aujourdhui || []).length > 0 && (
          <BlocManques testId="controle-manques-aujourdhui"
            fond={TERRA_FOND} bord="#e0b5a5" couleur={TERRA_SOMBRE}
            titre="Il manque pour cette arrivée"
            manques={menage.manques_aujourdhui} cfg={cfg} />
        )}
        {(menage.manques_prochaine_fois || []).length > 0 && (
          <BlocManques testId="controle-manques-prochaine"
            fond="#fdf7ee" bord="#d4a24c" couleur="#8a5f19"
            titre="Il manquera pour le prochain ménage"
            manques={menage.manques_prochaine_fois} cfg={cfg} />
        )}

        {/* b) LE BOUTON DE CHECKLIST — EN ALERTE quand une ligne manque
            (recalage 2026-08-21) : fond #fdf9f7, bordure #e0b5a5, icône et
            compte en #8f2f1d. */}
        {checklist && (
          <button type="button" data-testid="controle-voir-checklist"
            data-alerte={nonCochees > 0 ? '1' : '0'}
            onClick={() => setSousEcran('checklist')} className="fbz-press fbz-tap"
            style={{ width: '100%', minHeight: 52, display: 'flex', alignItems: 'center',
              gap: 11, padding: '11px 14px',
              background: nonCochees > 0 ? '#fdf9f7' : '#fff',
              border: `1.5px solid ${nonCochees > 0 ? '#e0b5a5' : CIEL}`,
              borderRadius: 12, textAlign: 'left',
              cursor: 'pointer', fontFamily: DS.fontBody, marginBottom: 14 }}>
            <Eye size={17} strokeWidth={2.1} color={nonCochees > 0 ? '#8f2f1d' : CIEL} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: C.navy }}>
                Voir ce que {prenomAgent} a coché
              </span>
              {/* ⚠️ DEUX VIDES OPPOSÉS (chantier Dimitri 2026-08-22) :
                    · aucune ligne CONFIGURÉE → trou de paramétrage FBZ, on ne
                      reproche rien à l'agent ;
                    · des lignes configurées et rien de coché → manquement, et
                      il se chiffre (« 0 sur 25 »).
                  `lignes_configurees` vient du serveur (checklist du logement,
                  héritage parent compris) — jamais deviné du côté client. */}
              <span data-testid="controle-compte-checklist"
                style={{ display: 'block', fontSize: 12, marginTop: 1,
                  color: configurees === 0 ? C.muet : (nonCochees > 0 ? '#8f2f1d' : VERT_SOMBRE),
                  fontWeight: nonCochees > 0 ? 700 : 600 }}>
                {(() => {
                  if (configurees === 0) return 'Aucune checklist pour ce logement.'
                  const coches = lignes.filter((l) => l.coche).length
                  // ⚠️ LE DÉNOMINATEUR EST LE TOTAL CONFIGURÉ, pas le nombre de
                  // lignes que l'agent a renseignées : sinon l'écran disait
                  // « 2 sur 3 » quand la carte disait « 2/18 » — deux comptes
                  // de la même chose, le piège qu'on chasse toute la journée.
                  const total = Math.max(configurees, lignes.length)
                  if (coches === 0) return `Checklist non remplie — 0 sur ${total}.`
                  const manquantes = total - coches
                  return `${coches} sur ${total} · ${manquantes > 0
                    ? `${manquantes} ligne${manquantes > 1 ? 's' : ''} non cochée${manquantes > 1 ? 's' : ''}`
                    : 'tout est coché'}`
                })()}
              </span>
            </span>
            <ChevronRight size={18} strokeWidth={2.2} color={C.muet} style={{ flexShrink: 0 }} />
          </button>
        )}

        {erreurEnvoi && (
          <BandeauErreurEnvoi texte={erreurEnvoi}
            onRetry={() => envoyerVerdict(menage, 'bon', [])} />
        )}

        {/* c) LES DEUX VERDICTS, avec leurs couleurs d'état. */}
        {checklist && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            <button type="button" data-testid="controle-rien-a-signaler"
              onClick={() => rienASignalerDepuisEcran(menage)} disabled={envoiEnCours}
              className="fbz-press fbz-tap"
              style={{ width: '100%', minHeight: CIBLE_VERDICT, border: 'none',
                borderRadius: 12, display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', gap: 8, background: VERT, color: VERT_SOMBRE,
                fontSize: 14.5, fontWeight: 700, fontFamily: DS.fontBody,
                cursor: 'pointer', opacity: envoiEnCours ? .6 : 1 }}>
              <CircleCheck size={18} strokeWidth={2.2} />
              Rien à signaler · le ménage est bon
            </button>
            <button type="button" data-testid="controle-signaler-points"
              onClick={() => { setSousEcran('categories'); setErreurEnvoi(null) }}
              disabled={envoiEnCours} className="fbz-press fbz-tap"
              style={{ width: '100%', minHeight: CIBLE_VERDICT, border: 'none',
                borderRadius: 12, display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', gap: 8, background: TERRA, color: '#fff',
                fontSize: 14.5, fontWeight: 700, fontFamily: DS.fontBody, cursor: 'pointer' }}>
              <ClipboardCheck size={18} strokeWidth={2.2} />
              Des points à reprendre · je vais dire lesquels
            </button>
          </div>
        )}
      </div>
    )
  }

  // ═══ LA LISTE « À CONTRÔLER » ═══
  const enAttente = menages || []
  const dejaTous = [...dejaLocal, ...dejaServeur]
  // « 21 août » — SANS jour de semaine (règle des dates, tri Dimitri
  // 2026-08-21), sur mobile comme en grand.
  const dateJour = new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric', month: 'long', timeZone: 'Europe/Paris',
  }).format(new Date())
  // L'ÉTAT du contrôle, pas seulement ce qui a été dit (Dimitri 2026-08-18) :
  // vert quand c'est CLOS, terracotta quand ça ATTEND encore — et depuis quand.
  const etatDeja = (d) => {
    const statut = d.statut || d.verdict || null
    const n = typeof d.nb_points === 'number' ? d.nb_points : null
    const pts = n != null ? `${n} point${n > 1 ? 's' : ''}` : 'points'
    // CONSULTATION d'un jour passé : un ménage JAMAIS contrôlé le dit — en
    // neutre (ni vert, ni terracotta : personne n'attend plus rien).
    if (statut === null || statut === undefined) return { clos: true, neutre: true, texte: 'jamais contrôlé' }
    if (statut === 'bon') return { clos: true, texte: 'rien à signaler' }
    if (statut === 'regle') return { clos: true, texte: `${pts} · repris` }
    if (statut === 'reprise_en_cours' || statut === 'a_reprendre') {
      const depuis = depuisTexte(d.controle_le)
      // Défensif : depuis le 2026-08-19 « je reprends » naît CLOS ('regle') —
      // cette branche ne peut servir que d'anciennes données.
      if (d.reprise_par === 'controleur') {
        return { clos: false, texte: `${pts} · je reprends${depuis ? ` · ${depuis}` : ''}` }
      }
      if (d.reprise_par === 'agent') {
        const qui = d.agent_prenom || "l'agent"
        return { clos: false, texte: `${pts} · ${qui} reprend${depuis ? ` · ${depuis}` : ''}` }
      }
      return { clos: false, texte: `${pts} · signalés${depuis ? ` · ${depuis}` : ''}` }
    }
    return { clos: false, texte: 'points signalés' }
  }
  // ══ LA BASCULE + LA VUE BILAN (avant la liste — en tête absolue). ══
  if (avecBilan && vue === 'bilan') {
    return (
      <div style={conteneur} data-testid="controle-ecran-bilan">
        <BasculeEspace testId="bascule-controle" actif={vue} onChange={setVue}
          entrees={[
            { id: 'a-controler', label: 'À contrôler', icone: Eye },
            { id: 'bilan', label: 'Le bilan', icone: ChartColumn },
          ]} />
        <ControleBilan cfg={cfg} />
      </div>
    )
  }

  return (
    <div style={conteneur} data-testid="controle-ecran-liste">
      {avecBilan && (
        <BasculeEspace testId="bascule-controle" actif={vue} onChange={setVue}
          entrees={[
            { id: 'a-controler', label: 'À contrôler', icone: Eye },
            { id: 'bilan', label: 'Le bilan', icone: ChartColumn },
          ]} />
      )}
      {/* RECALAGE 2026-08-21 : UNE SEULE FORME DE CARTES, partout — grille
          ≥ 900 px, colonne dessous. « Rien à signaler » a DISPARU de la liste
          (mobile compris) : le verdict vit derrière « Ouvrir ». Le bandeau à
          trois chiffres = pilotage (orchestrateur/admin) ; le contrôleur
          terrain garde son bandeau simple. */}
      <ControleListeLarge
sousBandeau={basculePage}         jour={jourVue}
        consultation={consultation}
        onJour={naviguerJour}
        menages={consultation ? [] : enAttente}
        nbFinis={enAttente.length + dejaTous.length}
        urgenceDe={(m) => urgenceDe(m.prochaine_arrivee, nowMin, today, m.heure_contrat, m.scheduled_date)}
        nomProduitDe={(csId) => nomProduit(cfg, csId)}
        nomDe={nomDe}
        agents={Object.entries(prenomsPool)
          .filter(([email]) => email !== (currentUser?.email || '').toLowerCase())
          .map(([email, prenom]) => ({ email, prenom: prenom || email.split('@')[0] }))}
        selfEmail={(currentUser?.email || '').toLowerCase()}
        peutDesigner={avecBilan}
        chiffres={avecBilan}
        estLarge={estLarge}
        sousTitreSimple={`${dateJour} · ${enAttente.length} en attente`}
        differes={differes}
        onAnnuler={annulerDiffere}
        onOuvrir={ouvrirControle}
        onDesigner={designerDepuisMenu}
      />

      {notice && (
        <div data-testid="controle-notice"
          style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12,
            padding: '10px 13px', borderRadius: 10, fontSize: 12.5, fontWeight: 600,
            background: notice.ton === 'ok' ? VERT_FOND : DS.warningBg,
            color: notice.ton === 'ok' ? VERT_SOMBRE : DS.warningText }}>
          {notice.ton === 'ok'
            ? <CircleCheck size={15} strokeWidth={2.2} style={{ flexShrink: 0 }} />
            : <CircleAlert size={15} strokeWidth={2.2} style={{ flexShrink: 0 }} />}
          {notice.texte}
        </div>
      )}

      {erreurListe && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: 14,
            background: DS.dangerBg, color: DS.dangerText, borderRadius: 10,
            fontSize: 13, lineHeight: 1.5 }}>
            <CircleAlert size={17} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
            {erreurListe}
          </div>
          <button onClick={chargerListe} className="fbz-press fbz-tap"
            style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 8,
              background: C.navy, color: '#fff', border: 'none',
              borderRadius: DS.radiusControl, padding: '11px 18px', fontSize: 13,
              fontWeight: 700, cursor: 'pointer', fontFamily: DS.fontBody }}>
            <RefreshCw size={16} strokeWidth={2} /> Réessayer
          </button>
        </div>
      )}


      {consultation && dejaTous.length === 0 && (
        <div data-testid="controle-jour-vide"
          style={{ padding: '26px 16px', textAlign: 'center', background: '#fff',
            border: '1px solid #d5e3ee', borderRadius: 12, fontSize: 13.5,
            color: DS.textSecondary, marginTop: 4 }}>
          Aucun ménage terminé ce jour-là.
        </div>
      )}

      {/* DÉJÀ CONTRÔLÉS — estompés : on ne contrôle pas deux fois sans le voir. */}
      {dejaTous.length > 0 && (
        <div style={{ marginTop: 20 }} data-testid="controle-deja-controles">
          <div style={{ fontSize: 10.5, fontWeight: 700, color: C.muet,
            textTransform: 'uppercase', letterSpacing: '.08em', margin: '0 2px 8px' }}>
            Déjà contrôlés
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {dejaTous.map((d) => {
              const etat = etatDeja(d)
              const teinte = etat.neutre
                ? { fond: '#f6f8fa', bord: '#e3e8ee', texte: '#5c6773' }
                : etat.clos
                  ? { fond: VERT_FOND, bord: VERT_BORD, texte: VERT_SOMBRE }
                  : { fond: TERRA_FOND, bord: TERRA_BORD, texte: TERRA_SOMBRE }
              return (
                <div key={d.run_id} data-testid={`controle-deja-${d.run_id}`}
                  data-clos={etat.clos ? 'oui' : 'non'}
                  style={{ background: teinte.fond, border: `1px solid ${teinte.bord}`,
                    borderRadius: 11, padding: '9px 13px',
                    // Vert = clos = estompé ; ce qui ATTEND reste net.
                    opacity: etat.clos ? .78 : 1,
                    display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: teinte.texte }}>
                      {nomDe(d)}
                    </span>
                    <span data-testid={`controle-deja-etat-${d.run_id}`}
                      style={{ fontSize: 11, color: teinte.texte, opacity: .9 }}>
                      {' — '}{etat.texte}
                    </span>
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* MES CONTRÔLES (GO Dimitri 2026-08-19) — le suivi AU-DELÀ des 72 h :
          « le contrôleur qui a signalé des points doit voir si sa remontée a
          été traitée, même une semaine après ». Section REPLIÉE qui ne charge
          RIEN avant ouverture ; les quatre états et le « depuis », même
          vocabulaire que « Déjà contrôlés ». */}
      <SectionMesControles nomDe={nomDe} />

      {/* LE PIED — pourquoi 72 h, dit une fois, en bas. */}
      <div data-testid="controle-pied"
        style={{ marginTop: 20, fontSize: 11.5, color: C.muet, textAlign: 'center',
          lineHeight: 1.55, padding: '0 10px' }}>
        Un ménage se contrôle jusqu'à l'heure d'arrivée du contrat, plus 12 h au
        maximum. Au-delà, sa journée reste consultable avec les flèches.
      </div>
    </div>
  )
}

// ── MES CONTRÔLES — repliée, 30 jours, les 4 états + « depuis ». ────────────
function SectionMesControles({ nomDe }) {
  const [ouvert, setOuvert] = useState(false)
  const [donnees, setDonnees] = useState(null) // null = jamais chargée
  const [chargement, setChargement] = useState(false)
  const [erreur, setErreur] = useState(null)

  useEffect(() => {
    if (!ouvert || donnees !== null) return
    let annule = false
    setChargement(true); setErreur(null)
    fetchMesControles()
      .then((json) => { if (!annule) setDonnees(Array.isArray(json?.controles) ? json.controles : []) })
      .catch((e) => {
        console.error('[controle] mes contrôles', e?.message || e)
        if (!annule) setErreur('Impossible de charger vos contrôles. Réessayez.')
      })
      .finally(() => { if (!annule) setChargement(false) })
    return () => { annule = true }
  }, [ouvert, donnees])

  const etatDe = (c) => {
    const n = typeof c.nb_points === 'number' ? c.nb_points : null
    const pts = n != null ? `${n} point${n > 1 ? 's' : ''}` : 'points'
    const depuis = depuisTexte(c.controle_le)
    if (c.statut === 'bon') return { clos: true, texte: 'rien à signaler' }
    if (c.statut === 'regle') return { clos: true, texte: `${pts} · repris` }
    if (c.reprise_par === 'controleur') {
      return { clos: false, texte: `${pts} · je reprends${depuis ? ` · ${depuis}` : ''}` }
    }
    const qui = c.agent_prenom || "l'agent"
    const faits = typeof c.nb_points_faits === 'number' && c.nb_points_faits > 0
      ? ` (${c.nb_points_faits}/${n} fait${c.nb_points_faits > 1 ? 's' : ''})` : ''
    return { clos: false, texte: `${pts} · ${qui} reprend${faits}${depuis ? ` · ${depuis}` : ''}` }
  }

  return (
    <div style={{ marginTop: 20 }}>
      <button type="button" data-testid="controle-mes-controles-toggle"
        onClick={() => setOuvert((o) => !o)} className="fbz-press fbz-tap"
        aria-expanded={ouvert}
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none',
          border: 'none', padding: '6px 2px', cursor: 'pointer', fontFamily: DS.fontBody,
          fontSize: 10.5, fontWeight: 700, color: C.muet, textTransform: 'uppercase',
          letterSpacing: '.08em' }}>
        <span aria-hidden="true" style={{ display: 'inline-block', fontSize: 9,
          transform: ouvert ? 'rotate(90deg)' : 'none', transition: 'transform .12s ease' }}>▶</span>
        Mes contrôles · 30 jours
      </button>

      {ouvert && (
        <div data-testid="controle-mes-controles">
          {chargement && <div style={{ fontSize: 12.5, color: DS.textMuted, padding: '4px 2px' }}>Chargement…</div>}
          {erreur && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '9px 12px',
              borderRadius: 10, fontSize: 12.5, background: DS.dangerBg, color: DS.dangerText }}>
              <CircleAlert size={14} strokeWidth={2.2} style={{ flexShrink: 0 }} />
              {erreur}
              <button type="button" data-testid="controle-mes-controles-reessayer"
                onClick={() => { setErreur(null); setDonnees(null) }}
                className="fbz-press fbz-tap"
                style={{ marginLeft: 'auto', background: 'none', border: 'none',
                  color: DS.dangerText, fontWeight: 700, fontSize: 12, cursor: 'pointer',
                  textDecoration: 'underline', fontFamily: DS.fontBody }}>
                Réessayer
              </button>
            </div>
          )}
          {donnees && donnees.length === 0 && !chargement && !erreur && (
            <div style={{ fontSize: 12.5, color: DS.textSecondary, padding: '4px 2px' }}>
              Aucun contrôle déposé ces 30 derniers jours.
            </div>
          )}
          {donnees && donnees.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 4 }}>
              {donnees.map((c) => {
                const etat = etatDe(c)
                const teinte = etat.clos
                  ? { fond: VERT_FOND, bord: VERT_BORD, texte: VERT_SOMBRE }
                  : { fond: TERRA_FOND, bord: TERRA_BORD, texte: TERRA_SOMBRE }
                return (
                  <div key={c.id} data-testid={`controle-mien-${c.id}`}
                    data-clos={etat.clos ? 'oui' : 'non'}
                    style={{ background: teinte.fond, border: `1px solid ${teinte.bord}`,
                      borderRadius: 11, padding: '9px 13px',
                      opacity: etat.clos ? .78 : 1 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: teinte.texte }}>
                      {nomDe(c)}
                    </span>
                    <span style={{ fontSize: 11, color: teinte.texte, opacity: .9 }}>
                      {' · '}{dateCourteControle(c.scheduled_date)}
                    </span>
                    <span data-testid={`controle-mien-etat-${c.id}`}
                      style={{ display: 'block', fontSize: 11, color: teinte.texte,
                        opacity: .9, marginTop: 2 }}>
                      {etat.texte}
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function BandeauErreurEnvoi({ texte, onRetry }) {
  return (
    <div style={{ marginTop: 14, marginBottom: 14 }}>
      <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: 13,
        background: DS.dangerBg, color: DS.dangerText, borderRadius: 10,
        fontSize: 13, lineHeight: 1.5 }}>
        <CircleAlert size={16} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
        {texte}
      </div>
      <button type="button" onClick={onRetry} data-testid="controle-reessayer-envoi"
        className="fbz-press fbz-tap"
        style={{ marginTop: 9, display: 'inline-flex', alignItems: 'center', gap: 7,
          background: C.navy, color: '#fff', border: 'none',
          borderRadius: DS.radiusControl, padding: '10px 16px', fontSize: 12.5,
          fontWeight: 700, cursor: 'pointer', fontFamily: DS.fontBody }}>
        <RefreshCw size={14} strokeWidth={2} /> Réessayer l'envoi
      </button>
    </div>
  )
}
